import { Campaign } from 'models/Campaign';
import { useQueryClient } from '@tanstack/react-query';
import { useTanstackMutation } from 'hooks';
import toast from 'react-hot-toast';
import {
  lauchCampaign as launchScrapingCampaign,
  pauseCampaign as pauseScrapingCampaign,
} from 'api/campaigns';
import useQueryParams from 'hooks/useQueryParams';
import { PaginatedData } from 'models/PaginatedData';
import { API_PATHS } from 'api';

type ToggleCampaignProps = {
  campaign: Campaign;
  isPersonal: boolean;
};

export function useToggleLaunchCampaign({
  campaign,
  isPersonal,
}: ToggleCampaignProps) {
  const queryClient = useQueryClient();
  const { id, isLaunched } = campaign;
  const { params } = useQueryParams();

  const { mutate: mutateCampaign, isPending } = useTanstackMutation<Campaign>({
    onSuccess: (campaign) => {
      queryClient.setQueryData(
        [`${isPersonal ? 'personal-' : ''}campaign`, { id: campaign.id }],
        campaign,
      );

      queryClient.setQueryData(
        [`${isPersonal ? 'personal-' : ''}campaigns`, { ...params, limit: 10 }],
        (oldData: PaginatedData<Campaign>) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            items: oldData?.items.map((campaignFromPage) =>
              campaignFromPage.id === id ? campaign : campaignFromPage,
            ),
          };
        },
      );

      toast.success(
        `Successfully ${isLaunched ? 'paused' : 'launched'} campaign.`,
      );
    },
    onError: (error) => {
      console.error(error);
      toast.error(`Error on ${isLaunched ? 'pausing' : 'launching'} campaign.`);
    },
  });

  const toggleLaunch = () => {
    const pauseCampaign = isPersonal
      ? API_PATHS.personalCampaigns.pauseCampaign
      : pauseScrapingCampaign;
    const launchCampaign = isPersonal
      ? API_PATHS.personalCampaigns.lauchCampaign
      : launchScrapingCampaign;
    mutateCampaign({
      path: isLaunched ? pauseCampaign(id) : launchCampaign(id),
      method: 'post',
    });
  };

  return { toggleLaunch, isPending };
}
